import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { borrowed, inventory } from '../services/api';
import { Plus, Package, Trash2, ChevronDown, ChevronRight, ArrowLeftRight, X, Check, MinusCircle } from 'lucide-react';
import { useToast } from '../context/ToastContext';

const DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '28', '30', '32', '34', '36', '38', '40', 'FREE'];

interface BorrowedItem {
  id: number; source_company: string; article_type: string; color: string;
  size: string; quantity: number; cost_price: number; created_at: string;
}

interface Settlement {
  article_type: string; color: string; size: string;
  total_borrowed: number; total_used: number; total_returned: number;
  still_owed: number; in_stock: number; cost_price: number;
  value_owed: number; value_used: number;
}

const BorrowedStock: React.FC = () => {
  const { toast, confirm } = useToast();
  const [items, setItems] = useState<BorrowedItem[]>([]);
  const [settlement, setSettlement] = useState<{ settlement: Settlement[]; summary: any } | null>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [tab, setTab] = useState<'stock' | 'settlement' | 'logs'>('stock');
  const [showAdd, setShowAdd] = useState(false);
  const [showReturn, setShowReturn] = useState<BorrowedItem | null>(null);
  const [returnQty, setReturnQty] = useState(1);
  const [returnNotes, setReturnNotes] = useState('');
  const [showDeduct, setShowDeduct] = useState<BorrowedItem | null>(null);
  const [deductQty, setDeductQty] = useState(1);
  const [deductNotes, setDeductNotes] = useState('');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [articleType, setArticleType] = useState('');
  const [selectedColor, setSelectedColor] = useState('Black');
  const [costPrice, setCostPrice] = useState('');
  const [sizeEntries, setSizeEntries] = useState(
    DEFAULT_SIZES.map(s => ({ size: s, qty: 0, enabled: false }))
  );
  const [availableCombos, setAvailableCombos] = useState<{article_type: string; color: string}[]>([]);
  const [selectedCombo, setSelectedCombo] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [itemsRes, settlementRes, logsRes, invRes] = await Promise.all([
        borrowed.getAll(), borrowed.getSettlement(), borrowed.getLogs(100), inventory.getAll(),
      ]);
      setItems(itemsRes.data);
      setSettlement(settlementRes.data);
      setLogs(logsRes.data);
      const seen = new Set<string>();
      const combos: {article_type: string; color: string}[] = [];
      (invRes.data || []).forEach((inv: any) => {
        const key = `${inv.article_type}|||${inv.color}`;
        if (!seen.has(key)) { seen.add(key); combos.push({ article_type: inv.article_type, color: inv.color }); }
      });
      combos.sort((a, b) => a.article_type.localeCompare(b.article_type) || a.color.localeCompare(b.color));
      setAvailableCombos(combos);
    } catch { toast('Failed to load borrowed stock data', 'error'); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const grouped = useMemo(() => {
    const groups: Record<string, { article_type: string; color: string; items: BorrowedItem[]; totalQty: number }> = {};
    const filtered = items.filter(i =>
      !filter || i.article_type.toLowerCase().includes(filter.toLowerCase()) ||
      i.color.toLowerCase().includes(filter.toLowerCase()) || i.size.toLowerCase().includes(filter.toLowerCase())
    );
    filtered.forEach(item => {
      const key = `${item.article_type}|||${item.color}`;
      if (!groups[key]) groups[key] = { article_type: item.article_type, color: item.color, items: [], totalQty: 0 };
      groups[key].items.push(item);
      groups[key].totalQty += item.quantity;
    });
    Object.values(groups).forEach(g => {
      g.items.sort((a, b) => {
        const ai = SIZE_ORDER.indexOf(a.size); const bi = SIZE_ORDER.indexOf(b.size);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
    });
    return Object.values(groups).sort((a, b) => a.article_type.localeCompare(b.article_type));
  }, [items, filter]);

  const totalQty = items.reduce((s, i) => s + i.quantity, 0);

  const handleAdd = async () => {
    const sizes: Record<string, number> = {};
    sizeEntries.filter(s => s.enabled && s.qty > 0).forEach(s => { sizes[s.size] = s.qty; });
    if (!articleType || Object.keys(sizes).length === 0) return;
    try {
      await borrowed.addBulk({ article_type: articleType, color: selectedColor, sizes, cost_price: parseFloat(costPrice) || 0 });
      setShowAdd(false); setArticleType(''); setCostPrice(''); setSelectedCombo('');
      setSizeEntries(DEFAULT_SIZES.map(s => ({ size: s, qty: 0, enabled: false })));
      toast('Borrowed stock added successfully', 'success');
      fetchAll();
    } catch (e: any) { toast(e?.response?.data?.detail || 'Failed to add borrowed stock', 'error'); }
  };

  const handleReturn = async () => {
    if (!showReturn || returnQty <= 0) return;
    try {
      await borrowed.returnStock(showReturn.id, { quantity: returnQty, notes: returnNotes || undefined });
      setShowReturn(null); setReturnQty(1); setReturnNotes('');
      toast('Stock returned successfully', 'success');
      fetchAll();
    } catch (e: any) { toast(e?.response?.data?.detail || 'Failed to return stock', 'error'); }
  };

  const handleDeduct = async () => {
    if (!showDeduct || deductQty <= 0) return;
    try {
      await borrowed.deductStock(showDeduct.id, { quantity: deductQty, notes: deductNotes || undefined });
      setShowDeduct(null); setDeductQty(1); setDeductNotes('');
      toast('Stock deducted successfully', 'success');
      fetchAll();
    } catch (e: any) { toast(e?.response?.data?.detail || 'Failed to deduct stock', 'error'); }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm('Delete this borrowed stock item? This cannot be undone.');
    if (!ok) return;
    try { await borrowed.delete(id); toast('Item deleted', 'success'); fetchAll(); }
    catch { toast('Failed to delete item', 'error'); }
  };

  const toggleExpand = (key: string) => {
    setExpanded(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const tabStyle = (t: string) => ({
    padding: '8px 18px', fontSize: 13, fontWeight: tab === t ? 600 : 400, cursor: 'pointer' as const,
    color: tab === t ? 'var(--accent-blue)' : 'var(--text-secondary)',
    borderBottom: tab === t ? '2px solid var(--accent-blue)' : '2px solid transparent',
    background: 'transparent', border: 'none', transition: 'var(--transition)',
  });

  return (
    <Layout title="Borrowed Stock" subtitle="Stock borrowed from Truegods">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'In Stock', value: totalQty, color: 'var(--accent-blue)' },
          { label: 'Total Used', value: settlement?.summary?.total_used || 0, color: 'var(--accent-purple)' },
          { label: 'Returned', value: settlement?.summary?.total_returned || 0, color: 'var(--accent-green)' },
          { label: 'Still Owed', value: settlement?.summary?.total_owed || 0, color: 'var(--accent-red)' },
          { label: 'Value Owed', value: `₹${(settlement?.summary?.total_value_owed || 0).toLocaleString('en-IN')}`, color: 'var(--accent-yellow)' },
        ].map((kpi, i) => (
          <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px 18px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{kpi.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setTab('stock')} style={tabStyle('stock')}>Stock</button>
          <button onClick={() => setTab('settlement')} style={tabStyle('settlement')}>Settlement</button>
          <button onClick={() => setTab('logs')} style={tabStyle('logs')}>Activity Log</button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter..."
            style={{ padding: '8px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, width: 160 }} />
          <button onClick={() => setShowAdd(true)} style={{
            padding: '8px 16px', background: 'var(--accent-blue)', color: '#fff', border: 'none',
            borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}><Plus size={14} /> Add Borrowed</button>
        </div>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</div> : (
        <>
          {tab === 'stock' && (
            grouped.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
                <ArrowLeftRight size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                <div style={{ fontSize: 15, fontWeight: 500 }}>No borrowed stock yet</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Add items borrowed from Truegods</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {grouped.map(group => {
                  const key = `${group.article_type}|||${group.color}`;
                  const isOpen = expanded.has(key);
                  return (
                    <div key={key} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                      <div onClick={() => toggleExpand(key)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', cursor: 'pointer' }}
                        onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)'}
                        onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {isOpen ? <ChevronDown size={16} color="var(--text-muted)" /> : <ChevronRight size={16} color="var(--text-muted)" />}
                          <Package size={16} color="var(--accent-blue)" />
                          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{group.article_type}</span>
                          <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-elevated)', padding: '2px 8px', borderRadius: 20 }}>{group.color}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: group.totalQty > 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>{group.totalQty} pcs</span>
                        </div>
                      </div>
                      {isOpen && (
                        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 18px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                            {group.items.map(item => (
                              <div key={item.id} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '12px', textAlign: 'center' }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{item.size}</div>
                                <div style={{ fontSize: 20, fontWeight: 700, color: item.quantity > 0 ? 'var(--accent-blue)' : 'var(--accent-red)' }}>{item.quantity}</div>
                                <div style={{ display: 'flex', gap: 4, marginTop: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                                  <button onClick={e => { e.stopPropagation(); setShowDeduct(item); setDeductQty(1); setDeductNotes(''); }} style={{
                                    padding: '4px 8px', background: 'rgba(157,122,255,0.1)', color: 'var(--accent-purple)', border: '1px solid rgba(157,122,255,0.2)',
                                    borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                                  }}>Deduct</button>
                                  <button onClick={e => { e.stopPropagation(); setShowReturn(item); setReturnQty(1); setReturnNotes(''); }} style={{
                                    padding: '4px 8px', background: 'var(--accent-green-soft)', color: 'var(--accent-green)', border: '1px solid rgba(45,212,160,0.2)',
                                    borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                                  }}>Return</button>
                                  <button onClick={e => { e.stopPropagation(); handleDelete(item.id); }} style={{
                                    padding: '4px 8px', background: 'var(--accent-red-soft)', color: 'var(--accent-red)', border: '1px solid rgba(239,90,111,0.2)',
                                    borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                                  }}><Trash2 size={10} /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}

          {tab === 'settlement' && settlement && (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
                      {['Article', 'Color', 'Size', 'Cost', 'Borrowed', 'Used', 'Returned', 'Owed', 'Value Owed'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {settlement.settlement.map((s, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                        onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)'}
                        onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                        <td style={{ padding: '11px 16px', fontWeight: 500, color: 'var(--text-primary)' }}>{s.article_type}</td>
                        <td style={{ padding: '11px 16px', color: 'var(--text-secondary)' }}>{s.color}</td>
                        <td style={{ padding: '11px 16px', color: 'var(--text-secondary)' }}>{s.size}</td>
                        <td style={{ padding: '11px 16px', color: 'var(--text-muted)', fontSize: 12 }}>₹{(s.cost_price || 0).toLocaleString('en-IN')}</td>
                        <td style={{ padding: '11px 16px', fontWeight: 600, color: 'var(--accent-blue)' }}>{s.total_borrowed}</td>
                        <td style={{ padding: '11px 16px', fontWeight: 600, color: 'var(--accent-purple)' }}>{s.total_used}</td>
                        <td style={{ padding: '11px 16px', fontWeight: 600, color: 'var(--accent-green)' }}>{s.total_returned}</td>
                        <td style={{ padding: '11px 16px', fontWeight: 700, color: s.still_owed > 0 ? 'var(--accent-red)' : 'var(--accent-green)' }}>{s.still_owed}</td>
                        <td style={{ padding: '11px 16px', fontWeight: 700, color: s.value_owed > 0 ? 'var(--accent-red)' : 'var(--accent-green)' }}>₹{(s.value_owed || 0).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                    {settlement.settlement.length === 0 && (
                      <tr><td colSpan={9} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No settlement data yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'logs' && (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
                      {['Action', 'Article', 'Color', 'Size', 'Qty', 'Order', 'Notes', 'Date'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => {
                      const ac: Record<string, string> = { added: 'var(--accent-blue)', deducted: 'var(--accent-purple)', returned: 'var(--accent-green)', adjusted: 'var(--accent-yellow)' };
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                          onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)'}
                          onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                          <td style={{ padding: '10px 16px' }}><span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: ac[log.action] || 'var(--text-secondary)', background: `${ac[log.action] || 'var(--text-secondary)'}15` }}>{log.action}</span></td>
                          <td style={{ padding: '10px 16px', color: 'var(--text-primary)', fontWeight: 500 }}>{log.article_type}</td>
                          <td style={{ padding: '10px 16px', color: 'var(--text-secondary)' }}>{log.color}</td>
                          <td style={{ padding: '10px 16px', color: 'var(--text-secondary)' }}>{log.size}</td>
                          <td style={{ padding: '10px 16px', fontWeight: 600 }}>{log.quantity}</td>
                          <td style={{ padding: '10px 16px', color: 'var(--accent-blue)', fontWeight: 500 }}>{log.order_name || '—'}</td>
                          <td style={{ padding: '10px 16px', color: 'var(--text-muted)', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.notes || '—'}</td>
                          <td style={{ padding: '10px 16px', color: 'var(--text-muted)', fontSize: 12 }}>{log.created_at ? new Date(log.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        </tr>
                      );
                    })}
                    {logs.length === 0 && (<tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No activity yet</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={() => setShowAdd(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', padding: 28, width: '90%', maxWidth: 480,
            border: '1px solid var(--border)', boxShadow: 'var(--shadow-elevated)', maxHeight: '85vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ color: 'var(--text-primary)', fontSize: 17, fontWeight: 700, margin: 0 }}>Add Borrowed Stock</h3>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} color="var(--text-muted)" /></button>
            </div>

            <div style={{ fontSize: 11, color: 'var(--accent-blue)', background: 'var(--accent-blue-soft)', padding: '8px 12px', borderRadius: 'var(--radius-sm)', marginBottom: 16, fontWeight: 500 }}>
              From: Truegods
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Select from Inventory</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 6 }}>
                {availableCombos.map((combo, i) => {
                  const key = `${combo.article_type}|||${combo.color}`;
                  const isSelected = selectedCombo === key;
                  return (
                    <div key={i} onClick={() => { setSelectedCombo(key); setArticleType(combo.article_type); setSelectedColor(combo.color); }} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '9px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                      background: isSelected ? 'var(--accent-blue-soft)' : 'transparent',
                      border: isSelected ? '1px solid rgba(79,125,249,0.3)' : '1px solid transparent',
                    }}
                      onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)'; }}
                      onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Package size={14} color={isSelected ? 'var(--accent-blue)' : 'var(--text-muted)'} />
                        <span style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? 'var(--accent-blue)' : 'var(--text-primary)' }}>{combo.article_type}</span>
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-elevated)', padding: '2px 10px', borderRadius: 20 }}>{combo.color}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Or enter manually</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={articleType} onChange={e => { setArticleType(e.target.value); setSelectedCombo(''); }} placeholder="Article type"
                  style={{ flex: 1, padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
                <input value={selectedColor} onChange={e => { setSelectedColor(e.target.value); setSelectedCombo(''); }} placeholder="Color"
                  style={{ flex: 1, padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Cost Price (per unit)</label>
              <input value={costPrice} onChange={e => setCostPrice(e.target.value)} type="number" placeholder="0"
                style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>Sizes & Quantities</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {sizeEntries.map((entry, idx) => (
                  <div key={entry.size} onClick={() => { const n = [...sizeEntries]; n[idx].enabled = !n[idx].enabled; setSizeEntries(n); }} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                    background: entry.enabled ? 'var(--accent-blue-soft)' : 'var(--bg-elevated)',
                    border: `1px solid ${entry.enabled ? 'rgba(79,125,249,0.3)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: entry.enabled ? 'var(--accent-blue)' : 'transparent',
                      border: entry.enabled ? 'none' : '1.5px solid var(--border)',
                    }}>{entry.enabled && <Check size={10} color="#fff" />}</div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', width: 28 }}>{entry.size}</span>
                    {entry.enabled && (
                      <input type="number" value={entry.qty || ''} onClick={e => e.stopPropagation()}
                        onChange={e => { const n = [...sizeEntries]; n[idx].qty = parseInt(e.target.value) || 0; setSizeEntries(n); }}
                        style={{ width: 45, padding: '4px 6px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)', fontSize: 12, textAlign: 'center' }}
                        placeholder="0" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <button onClick={handleAdd} style={{
              width: '100%', padding: '12px', background: 'var(--accent-blue)', color: '#fff', border: 'none',
              borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>Add Borrowed Stock</button>
          </div>
        </div>
      )}

      {showDeduct && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={() => setShowDeduct(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', padding: 28, width: '90%', maxWidth: 400,
            border: '1px solid var(--border)', boxShadow: 'var(--shadow-elevated)',
          }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: 17, fontWeight: 700, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <MinusCircle size={18} color="var(--accent-purple)" /> Deduct from Borrowed Stock
            </h3>
            <div style={{ background: 'var(--bg-elevated)', padding: 12, borderRadius: 'var(--radius-sm)', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{showDeduct.article_type} — {showDeduct.color} — {showDeduct.size}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Available: {showDeduct.quantity} units</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Quantity to Deduct (sold/used)</label>
              <input type="number" min={1} max={showDeduct.quantity} value={deductQty} onChange={e => setDeductQty(parseInt(e.target.value) || 0)}
                style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Notes (optional)</label>
              <input value={deductNotes} onChange={e => setDeductNotes(e.target.value)} placeholder="e.g. Used for order #1234"
                style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowDeduct(null)} style={{ flex: 1, padding: '10px', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDeduct} disabled={deductQty <= 0 || deductQty > showDeduct.quantity} style={{ flex: 1, padding: '10px', background: 'linear-gradient(135deg, var(--accent-purple), #7c3aed)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Confirm Deduct</button>
            </div>
          </div>
        </div>
      )}

      {showReturn && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={() => setShowReturn(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', padding: 28, width: '90%', maxWidth: 400,
            border: '1px solid var(--border)', boxShadow: 'var(--shadow-elevated)',
          }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: 17, fontWeight: 700, margin: '0 0 16px' }}>Return Stock</h3>
            <div style={{ background: 'var(--bg-elevated)', padding: 12, borderRadius: 'var(--radius-sm)', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{showReturn.article_type} — {showReturn.color} — {showReturn.size}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Current qty: {showReturn.quantity}</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Quantity to Return</label>
              <input type="number" value={returnQty} onChange={e => setReturnQty(parseInt(e.target.value) || 0)}
                style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Notes (optional)</label>
              <input value={returnNotes} onChange={e => setReturnNotes(e.target.value)} placeholder="e.g. Returned via courier"
                style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowReturn(null)} style={{ flex: 1, padding: '10px', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleReturn} style={{ flex: 1, padding: '10px', background: 'var(--accent-green)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Confirm Return</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default BorrowedStock;
